import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Zap } from "lucide-react";
import { API } from "@/config/api";

const SelectTier_v2 = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  const tiers = [
    {
      id: "essential",
      name: "Essential",
      price: 9.99,
      coverageAmount: 150,
      features: ["Basic protection", "Email support", "Same-day review"]
    },
    {
      id: "standard",
      name: "Standard",
      price: 19.99,
      coverageAmount: 300,
      features: ["Enhanced protection", "Priority support", "Fast-track review", "Flexible repayment"],
      featured: true
    },
    {
      id: "plus",
      name: "Plus",
      price: 39.99,
      coverageAmount: 600,
      features: ["Maximum protection", "24/7 support", "Instant review", "Custom repayment plans"]
    }
  ];

  const handleSelectTier = async (tier) => {
    setSelectedTier(tier.id);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/subscription/create-checkout`, 
        { 
          tier: tier.id,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setLoading(false);
      setSelectedTier(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-6">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Zap className="h-12 w-12 text-yellow-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4 text-white" data-testid="select-tier-title">
            Pick Your Power Level
          </h1>
          <p className="text-xl text-slate-400">
            All plans include human review, flexible repayment, and member-friendly terms.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tiers.map((tier) => (
            <div 
              key={tier.id}
              data-testid={`tier-card-${tier.id}`}
              className={`relative overflow-hidden rounded-3xl p-8 border-2 transition-all ${
                tier.featured
                  ? "border-yellow-400 bg-gradient-to-br from-yellow-400/10 to-orange-500/10 scale-105 shadow-2xl"
                  : "border-slate-700 bg-slate-800 hover:border-yellow-400/50"
              }`}
            >
              {tier.featured && (
                <div className="absolute top-0 right-0 bg-yellow-400 text-black px-4 py-1 text-sm font-bold rounded-bl-lg">
                  🔥 MOST PICKED
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="text-2xl font-heading font-bold mb-2 text-white">{tier.name}</h3>
                <div className="text-5xl font-heading font-bold text-yellow-400 mb-2">
                  ${tier.price}<span className="text-2xl text-slate-400">/mo</span>
                </div>
                <p className="text-sm text-slate-400">
                  Up to <span className="text-yellow-400 font-bold text-lg">${tier.coverageAmount}</span> per bill
                </p>
              </div>
              <ul className="space-y-3 mb-8">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                data-testid={`subscribe-${tier.id}-btn`}
                onClick={() => handleSelectTier(tier)}
                className={`w-full rounded-full py-6 text-lg font-bold ${
                  tier.featured
                    ? "bg-yellow-400 text-black hover:bg-yellow-300"
                    : "bg-slate-700 text-white hover:bg-slate-600"
                }`}
                disabled={loading && selectedTier === tier.id}
              >
                {loading && selectedTier === tier.id ? 'Processing...' : `Lock In ${tier.name}`}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-slate-400">
            Payments are securely processed by Stripe. You can cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SelectTier_v2;
